import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, X, AlertTriangle, CheckCircle2, Plus, Camera, Upload, Trash2, ImageIcon, ShieldAlert, ShieldCheck } from 'lucide-react';
import jalaali from 'jalaali-js';
import {
  advancesApi,
  saveBsiTransferPrefill,
  type AdvanceEligibility,
  type AdvanceMeta,
  type EmployeeAdvance,
  type EmployeeAdvancePaymentType,
} from '@/lib/api/advances';
import { accountingApi } from '@/lib/api/accounting';
import { bsiInternetBankApi, type BsiIbStatus } from '@/lib/banks/bsi/api';
import { getBackendBaseUrl } from '@/lib/api/config';
import { formatRial } from '@/lib/formatMoney';
import PersianDatePickerFa from '@/components/shared/PersianDatePickerFa';
import PersianMonthPickerFa, { monthApiToDisplay } from '@/components/shared/PersianMonthPickerFa';
import SearchableSelect from '@/components/shared/SearchableSelect';
import BankAccountModal from './BankAccountModal';
import CameraCaptureModal from './CameraCaptureModal';
import ReceiptCropModal from './ReceiptCropModal';
import { bsiTransferModeLabel, bsiLocalDestinationMissingAccount, resolveBsiTransferMode } from '@/lib/bsiTransferMode';
import { bsiServicePath } from '@/pages/banks/bsi/helpers';

function currentJalaliDate(): string {
  const { jy, jm, jd } = jalaali.toJalaali(new Date());
  return `${jy}/${String(jm).padStart(2, '0')}/${String(jd).padStart(2, '0')}`;
}

function currentJalaliMonthApi(): string {
  const { jy, jm } = jalaali.toJalaali(new Date());
  return `${jy}-${String(jm).padStart(2, '0')}`;
}

function parseAmountInput(raw: string): number {
  const digits = raw.replace(/[^\d۰-۹]/g, '').replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
  return digits ? parseInt(digits, 10) : 0;
}

function formatAmountInput(n: number): string {
  if (!n) return '';
  return formatRial(n);
}

function toJalaliDateValue(value?: string): string {
  if (!value) return currentJalaliDate();
  if (value.includes('/')) return value.slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return currentJalaliDate();
  const { jy, jm, jd } = jalaali.toJalaali(parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10));
  return `${jy}/${String(jm).padStart(2, '0')}/${String(jd).padStart(2, '0')}`;
}

function payrollMonthToApi(month: string): string {
  return month.replace(/\//g, '-').slice(0, 7);
}

function resolveAdvanceAttachmentUrl(a: Pick<EmployeeAdvance, 'attachment_url' | 'attachment_path'>): string | null {
  if (a.attachment_url) {
    if (a.attachment_url.startsWith('http')) return a.attachment_url;
    const base = getBackendBaseUrl();
    return base ? `${base}${a.attachment_url}` : a.attachment_url;
  }
  if (!a.attachment_path) return null;
  const base = getBackendBaseUrl();
  return base ? `${base}/storage/${a.attachment_path}` : `/storage/${a.attachment_path}`;
}

interface AdvanceModalProps {
  show: boolean;
  employeeId: number;
  editAdvance?: EmployeeAdvance | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AdvanceModal: React.FC<AdvanceModalProps> = ({
  show,
  employeeId,
  editAdvance = null,
  onClose,
  onSuccess,
}) => {
  const isEdit = !!editAdvance;
  const [meta, setMeta] = useState<AdvanceMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [projectId, setProjectId] = useState<number | ''>('');
  const [paymentType, setPaymentType] = useState<EmployeeAdvancePaymentType>('advance');
  const [expenseTypeId, setExpenseTypeId] = useState<number | ''>('');
  const [executePayment, setExecutePayment] = useState(false);
  const [amount, setAmount] = useState(0);
  const [amountDisplay, setAmountDisplay] = useState('');
  const [registeredAt, setRegisteredAt] = useState(currentJalaliDate);
  const [payrollMonth, setPayrollMonth] = useState(currentJalaliMonthApi);
  const [sourceBankId, setSourceBankId] = useState<number | ''>('');
  const [destBankId, setDestBankId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [eligibility, setEligibility] = useState<AdvanceEligibility | null>(null);
  const [eligLoading, setEligLoading] = useState(false);
  const [eligError, setEligError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [bsiStatus, setBsiStatus] = useState<BsiIbStatus | null>(null);
  const [bsiStatusLoading, setBsiStatusLoading] = useState(false);

  const needsBankSession = !isEdit && executePayment;
  const bsiReady = Boolean(bsiStatus?.configured && bsiStatus?.session_valid);

  const loadBsiStatus = useCallback(async () => {
    setBsiStatusLoading(true);
    try {
      const res = await bsiInternetBankApi.status();
      setBsiStatus(res.data);
    } catch {
      setBsiStatus(null);
    } finally {
      setBsiStatusLoading(false);
    }
  }, []);

  const [showBankModal, setShowBankModal] = useState(false);
  const [banks, setBanks] = useState<{ id: number; name: string }[]>([]);
  const [showCamera, setShowCamera] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [cropSrcUrl, setCropSrcUrl] = useState<string | null>(null);
  const [cropOriginalFile, setCropOriginalFile] = useState<File | null>(null);
  const [showCrop, setShowCrop] = useState(false);
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState<string | null>(null);
  const [existingAttachmentName, setExistingAttachmentName] = useState<string | null>(null);
  const [removeExistingAttachment, setRemoveExistingAttachment] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const applyMeta = useCallback(
    (data: AdvanceMeta | undefined | null, preferProjectId?: number, preserveBanks = false) => {
      if (!data) return;
      setMeta(data);
      const projects = data.projects ?? [];
      if (preferProjectId && projects.some((p) => p.id === preferProjectId)) {
        setProjectId(preferProjectId);
      } else if (!preferProjectId && projects.length >= 1) {
        // اولین آیتم = آخرین پروژه با کارکرد
        setProjectId(projects[0].id);
      } else if (projects.length === 0 && !preferProjectId) {
        setProjectId('');
      }

      if (!preserveBanks) {
        // مبدأ/مقصد اختیاری‌اند؛ برای خرید و پرداخت‌های غیرحقوقی پیش‌فرض خالی می‌ماند
        setSourceBankId('');
        setDestBankId('');
      }
    },
    []
  );

  const reloadMeta = useCallback(
    async (preferProjectId?: number) => {
      const r = await advancesApi.meta(employeeId);
      applyMeta(r.data, preferProjectId);
      return r.data;
    },
    [employeeId, applyMeta]
  );

  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    setFormError('');
    setEligibility(null);
    setShowBankModal(false);
    setShowCamera(false);
    setShowCrop(false);
    setCropOriginalFile(null);
    setFile(null);
    setRemoveExistingAttachment(false);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setCropSrcUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    if (editAdvance) {
      setAmount(editAdvance.amount);
      setAmountDisplay(formatAmountInput(editAdvance.amount));
      setRegisteredAt(toJalaliDateValue(editAdvance.registered_at));
      setPayrollMonth(editAdvance.payroll_month ? payrollMonthToApi(editAdvance.payroll_month) : '');
      setProjectId(editAdvance.project_id);
      setSourceBankId(editAdvance.source_bank_account_id ?? '');
      setDestBankId(editAdvance.destination_employee_bank_account_id ?? '');
      setNotes(editAdvance.notes ?? '');
      setPaymentType(
        editAdvance.payment_type === 'on_account' || editAdvance.payment_type === 'other'
          ? (editAdvance.payment_type as 'on_account' | 'other')
          : 'advance',
      );
      setExpenseTypeId(editAdvance.expense_type_id ?? '');
      setExecutePayment(false);
      setExistingAttachmentUrl(resolveAdvanceAttachmentUrl(editAdvance));
      setExistingAttachmentName(editAdvance.attachment_name ?? null);
    } else {
      setAmount(0);
      setAmountDisplay('');
      setRegisteredAt(currentJalaliDate());
      setPayrollMonth('');
      setSourceBankId('');
      setDestBankId('');
      setNotes('');
      setProjectId('');
      setPaymentType('advance');
      setExpenseTypeId('');
      setExecutePayment(false);
      setExistingAttachmentUrl(null);
      setExistingAttachmentName(null);
    }

    setMetaLoading(true);

    Promise.all([
      advancesApi.meta(employeeId),
      accountingApi.employeeBankAccounts(employeeId).catch(() => ({ banks: [] as { id: number; name: string }[] })),
    ])
      .then(([metaRes, bankRes]) => {
        applyMeta(metaRes.data, editAdvance?.project_id, !!editAdvance);
        setBanks(bankRes.banks ?? []);
      })
      .catch((e) => setFormError(e instanceof Error ? e.message : 'خطا در بارگذاری فرم'))
      .finally(() => setMetaLoading(false));
  }, [show, employeeId, editAdvance, applyMeta]);

  useEffect(() => {
    if (!show || isEdit || !meta?.expense_types?.length) return;
    if (typeof expenseTypeId === 'number') return;
    const types = meta.expense_types;
    const p = typeof projectId === 'number' ? meta.projects?.find((x) => x.id === projectId) : undefined;
    const preferred = p?.is_contractor
      ? types.find((t) => /پیمانکار/u.test(t.title)) ?? types.find((t) => !t.caps_to_earned)
      : types.find((t) => /حقوق|دستمزد/u.test(t.title)) ?? types.find((t) => t.caps_to_earned);
    const pick = preferred ?? types[0];
    if (pick) {
      setExpenseTypeId(pick.id);
      setPaymentType(pick.caps_to_earned ? 'advance' : 'on_account');
    }
  }, [show, isEdit, projectId, meta?.expense_types, meta?.projects, expenseTypeId]);

  useEffect(() => {
    if (!show || !needsBankSession) {
      setBsiStatus(null);
      setBsiStatusLoading(false);
      return;
    }
    void loadBsiStatus();
  }, [show, needsBankSession, loadBsiStatus]);

  useEffect(() => {
    if (!show || !needsBankSession) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void loadBsiStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [show, needsBankSession, loadBsiStatus]);

  const expenseTypes = meta?.expense_types ?? [];
  const selectedExpenseType =
    typeof expenseTypeId === 'number' ? expenseTypes.find((t) => t.id === expenseTypeId) : undefined;
  const needsPayrollCap = selectedExpenseType?.caps_to_earned ?? paymentType === 'advance';
  const isOnAccount = !needsPayrollCap;
  const paymentTypeLabel = selectedExpenseType?.title
    ?? (isOnAccount ? 'علی‌الحساب' : paymentType === 'other' ? 'سایر پرداخت' : 'مساعده');
  const selectedMetaProject =
    typeof projectId === 'number' ? meta?.projects?.find((p) => p.id === projectId) : undefined;

  useEffect(() => {
    if (!show || !needsPayrollCap || projectId === '' || !payrollMonth) {
      setEligibility(null);
      setEligError(null);
      setEligLoading(false);
      return;
    }
    let cancelled = false;
    setEligLoading(true);
    setEligError(null);
    advancesApi
      .eligibility(employeeId, {
        project_id: projectId,
        month: monthApiToDisplay(payrollMonth),
        exclude_advance_id: editAdvance?.id,
      })
      .then((r) => {
        if (!cancelled) {
          setEligibility(r.data ?? null);
          setEligError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setEligibility(null);
          setEligError(e instanceof Error ? e.message : 'محاسبه سقف ممکن نشد.');
        }
      })
      .finally(() => {
        if (!cancelled) setEligLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, employeeId, projectId, payrollMonth, editAdvance?.id, needsPayrollCap]);

  const projectOptions = useMemo(() => {
    const list = [...(meta?.projects ?? [])];
    if (editAdvance && !list.some((p) => p.id === editAdvance.project_id)) {
      list.unshift({
        id: editAdvance.project_id,
        name: editAdvance.project?.name ?? `پروژه ${editAdvance.project_id}`,
        client_name: null,
      });
    }
    return list.map((p) => ({
      value: String(p.id),
      label: p.client_name ? `${p.name} — ${p.client_name}` : p.name,
      searchText: `${p.name} ${p.client_name ?? ''}`,
    }));
  }, [meta?.projects, editAdvance]);

  const setAttachment = (next: File | null) => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      if (next && next.type.startsWith('image/')) {
        return URL.createObjectURL(next);
      }
      return null;
    });
    setFile(next);
  };

  /** تصویر → اول کراپ چهارگوشه؛ PDF مستقیم ذخیره */
  const beginAttachment = (next: File | null) => {
    if (!next) {
      setAttachment(null);
      return;
    }
    setRemoveExistingAttachment(false);
    if (next.type.startsWith('image/')) {
      setCropSrcUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(next);
      });
      setCropOriginalFile(next);
      setShowCrop(true);
      return;
    }
    setAttachment(next);
  };

  const clearAttachment = () => {
    setAttachment(null);
    if (existingAttachmentUrl) {
      setRemoveExistingAttachment(true);
      setExistingAttachmentUrl(null);
      setExistingAttachmentName(null);
    }
  };

  const closeCrop = () => {
    setShowCrop(false);
    setCropOriginalFile(null);
    setCropSrcUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const selectedDestBank = useMemo(() => {
    if (typeof destBankId !== 'number') return null;
    return (meta?.employee_bank_accounts ?? []).find((b) => b.id === destBankId) ?? null;
  }, [destBankId, meta?.employee_bank_accounts]);

  const selectedTransferMode = useMemo(() => {
    if (!selectedDestBank || !executePayment || isEdit) return null;
    return resolveBsiTransferMode(selectedDestBank);
  }, [selectedDestBank, executePayment, isEdit]);

  if (!show) return null;

  const remaining = eligibility?.remaining_eligible ?? 0;
  const exceeds = needsPayrollCap && amount > 0 && amount > remaining;
  const selectedProjectId = typeof projectId === 'number' ? projectId : null;
  const canSubmit =
    selectedProjectId != null &&
    amount > 0 &&
    !!registeredAt &&
    (!needsPayrollCap || !!payrollMonth) &&
    (expenseTypes.length === 0 || typeof expenseTypeId === 'number') &&
    !exceeds &&
    !loading &&
    !metaLoading &&
    (!executePayment || sourceBankId !== '') &&
    (!needsBankSession || (!bsiStatusLoading && bsiReady)) &&
    !(needsBankSession && selectedDestBank && bsiLocalDestinationMissingAccount(selectedDestBank));

  const handleBankSuccess = async () => {
    try {
      await reloadMeta(selectedProjectId ?? undefined);
      const bankRes = await accountingApi.employeeBankAccounts(employeeId).catch(() => ({ banks: [] }));
      setBanks(bankRes.banks ?? banks);
    } catch {
      /* ignore */
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || selectedProjectId == null) return;
    if (needsBankSession && !bsiReady) {
      setFormError(
        bsiStatus?.configured === false
          ? 'اینترنت‌بانک صادرات پیکربندی نشده است. ابتدا اتصال بانک را از بخش اینترنت‌بانک تنظیم کنید.'
          : 'برای پرداخت مستقیم بانکی، ابتدا در اینترنت‌بانک صادرات وارد شوید، سپس برگردید و پرداخت را ثبت کنید.',
      );
      return;
    }
    setLoading(true);
    setFormError('');
    try {
      const fd = new FormData();
      fd.append('project_id', String(selectedProjectId));
      fd.append('amount', String(amount));
      fd.append('registered_at', registeredAt);
      if (payrollMonth) {
        fd.append('payroll_month', monthApiToDisplay(payrollMonth));
      }
      if (typeof expenseTypeId === 'number') {
        fd.append('expense_type_id', String(expenseTypeId));
      }
      fd.append(
        'payment_type',
        needsPayrollCap ? 'advance' : selectedExpenseType && /پیمانکار|علی.?الحساب/u.test(selectedExpenseType.title)
          ? 'on_account'
          : 'other',
      );
      if (!isEdit) {
        fd.append('execute_payment', executePayment ? '1' : '0');
      }
      if (typeof sourceBankId === 'number') {
        fd.append('source_bank_account_id', String(sourceBankId));
      }
      if (typeof destBankId === 'number') {
        fd.append('destination_employee_bank_account_id', String(destBankId));
      }
      if (notes.trim()) fd.append('notes', notes.trim());
      if (file) fd.append('attachment', file);
      if (isEdit && editAdvance) {
        if (removeExistingAttachment && !file) {
          fd.append('remove_attachment', '1');
        }
        await advancesApi.update(employeeId, editAdvance.id, fd);
        onSuccess();
        onClose();
      } else {
        const res = await advancesApi.store(employeeId, fd);
        const hint = res.data?.payment?.transfer_hint;
        if (hint) {
          saveBsiTransferPrefill(hint);
        }
        onSuccess();
        onClose();
        if (executePayment && hint) {
          const tab = hint.suggested_bsi_mode === 'interbank' ? 'interbank' : 'transfer';
          const goBsi = window.confirm(
            'پرداخت از حساب مبدأ ثبت شد. برای تکمیل انتقال بانکی به اینترنت‌بانک بروید؟',
          );
          if (goBsi) {
            window.location.href = bsiServicePath(tab);
          }
        }
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : isEdit ? 'خطا در ویرایش مساعده' : 'خطا در ثبت مساعده');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
        dir="rtl"
        onClick={onClose}
      >
        <div
          className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 items-center justify-between border-b bg-white p-4">
            <h3 className="text-lg font-bold text-gray-800">
              {isEdit ? `ویرایش ${paymentTypeLabel}` : `ثبت ${paymentTypeLabel}`}
            </h3>
            <button type="button" onClick={onClose} className="rounded p-2 hover:bg-gray-100">
              <X size={20} />
            </button>
          </div>

          {metaLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="animate-spin text-sky-600" size={32} />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain p-4">
              {formError ? (
                <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {formError}
                </p>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">پروژه *</label>
                {metaLoading ? (
                  <p className="text-xs text-slate-500 px-1 py-2">در حال بارگذاری پروژه‌ها…</p>
                ) : (meta?.projects?.length ?? 0) > 0 ? (
                  <SearchableSelect
                    inModal
                    value={projectId === '' ? '' : String(projectId)}
                    onChange={(v) => {
                      setProjectId(v ? parseInt(v, 10) : '');
                      if (!isEdit) setExpenseTypeId('');
                    }}
                    options={projectOptions}
                    placeholder="جست‌وجو و انتخاب پروژه..."
                    emptyMessage="پروژه‌ای یافت نشد"
                  />
                ) : formError ? (
                  <p className="text-xs text-rose-800 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                    لیست پروژه‌ها بارگذاری نشد. صفحه را رفرش کنید یا دوباره تلاش کنید.
                  </p>
                ) : (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    این کارمند به هیچ پروژهٔ فعالی (در حال اجرا / برنامه‌ریزی / متوقف) تخصیص ندارد. ابتدا در مدیریت
                    پروژه او را به پروژه اختصاص دهید.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ماه کارکرد{needsPayrollCap ? ' *' : ''}
                  </label>
                  <PersianMonthPickerFa
                    value={payrollMonth}
                    onChange={setPayrollMonth}
                    placeholder={needsPayrollCap ? 'انتخاب ماه…' : 'اختیاری — برای خرید لازم نیست'}
                  />
                  {!needsPayrollCap ? (
                    <p className="mt-1 text-[11px] text-slate-500">
                      برای خرید و هزینه‌های غیرحقوقی، ماه کارکرد لازم نیست.
                    </p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاریخ ثبت *</label>
                  <PersianDatePickerFa value={registeredAt} onChange={setRegisteredAt} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">نوع هزینه *</label>
                {expenseTypes.length > 0 ? (
                  <SearchableSelect
                    inModal
                    value={expenseTypeId === '' ? '' : String(expenseTypeId)}
                    onChange={(v) => {
                      const id = v ? parseInt(v, 10) : NaN;
                      if (!Number.isFinite(id)) {
                        setExpenseTypeId('');
                        return;
                      }
                      setExpenseTypeId(id);
                      const t = expenseTypes.find((x) => x.id === id);
                      setPaymentType(t?.caps_to_earned ? 'advance' : 'on_account');
                    }}
                    options={expenseTypes.map((t) => ({
                      value: String(t.id),
                      label: t.title,
                      searchText: `${t.title} ${t.note ?? ''}`,
                    }))}
                    placeholder="انتخاب نوع هزینه..."
                    emptyMessage="نوع هزینه‌ای یافت نشد"
                  />
                ) : (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    نوع هزینه‌ای در سامانه تعریف نشده. از بخش نوع هزینه‌ها اضافه کنید.
                  </p>
                )}
                {selectedExpenseType?.note ? (
                  <p className="mt-1.5 text-xs text-slate-500">{selectedExpenseType.note}</p>
                ) : null}
                {selectedMetaProject?.is_contractor && selectedExpenseType?.caps_to_earned ? (
                  <p className="mt-1.5 text-xs text-violet-700">
                    برای پیمانکار معمولاً نوع «پرداخت پیمانکار جزء» مناسب‌تر است.
                  </p>
                ) : null}
                {(eligibility?.attendance_earned ?? 0) > 0 && (eligibility?.resource_earned ?? 0) > 0 ? (
                  <p className="mt-1.5 text-xs text-slate-600">
                    این نفر هم حضور دارد هم کارکرد منبع. برای حقوق/مساعده سقف مجموع هر دو است؛ برای
                    «پرداخت پیمانکار» سقف اعمال نمی‌شود و از سقف مساعده کم نمی‌کند.
                  </p>
                ) : null}
              </div>

              {needsPayrollCap ? (
                <div className="rounded-xl border border-sky-100 bg-sky-50/70 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-sky-900">
                    {eligLoading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                    سقف پرداخت بر اساس حضور و کارکرد منابع
                  </div>
                  {!projectId || !payrollMonth ? (
                    <p className="text-xs text-sky-800/80">پروژه و ماه را انتخاب کنید تا سقف محاسبه شود.</p>
                  ) : eligibility ? (
                    <div className="grid grid-cols-2 gap-2 text-xs text-sky-950">
                      <div>
                        روز کارکرد: <b>{eligibility.worked_days}</b>
                      </div>
                      <div>
                        کارکرد حضور: <b>{formatRial(eligibility.attendance_earned ?? 0)} ریال</b>
                      </div>
                      <div>
                        کارکرد منابع: <b>{formatRial(eligibility.resource_earned ?? 0)} ریال</b>
                        {(eligibility.resource_count ?? 0) > 0 ? (
                          <span className="text-sky-700/70"> ({eligibility.resource_count} منبع)</span>
                        ) : null}
                      </div>
                      <div>
                        مجموع کارکرد: <b>{formatRial(eligibility.earned_salary)} ریال</b>
                      </div>
                      <div>
                        پرداخت قبلی (سقف‌دار): <b>{formatRial(eligibility.previous_advances)} ریال</b>
                      </div>
                      <div>
                        باقیمانده مجاز:{' '}
                        <b className="text-emerald-700">{formatRial(eligibility.remaining_eligible)} ریال</b>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-rose-600">{eligError || 'محاسبه سقف ممکن نشد.'}</p>
                  )}
                </div>
              ) : (
                <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  این نوع هزینه سقف کارکرد ندارد؛ مبدأ و مقصد بانکی هم اختیاری است مگر اینکه «کسر از حساب و انتقال بانکی» را
                  فعال کنید.
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">مبلغ (ریال) *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={amountDisplay}
                  onChange={(e) => {
                    const n = parseAmountInput(e.target.value);
                    setAmount(n);
                    setAmountDisplay(formatAmountInput(n));
                  }}
                  className={`w-full border rounded-lg px-3 py-2 font-mono ${
                    exceeds ? 'border-rose-400 bg-rose-50' : 'border-gray-300'
                  }`}
                  placeholder="مثلاً ۵٬۰۰۰٬۰۰۰"
                  required
                />
                {exceeds && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-rose-600">
                    <AlertTriangle size={14} />
                    مبلغ از باقیمانده مجاز ({formatRial(remaining)} ریال) بیشتر است.
                  </p>
                )}
              </div>

              {!isEdit ? (
                <label className="flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2.5 text-sm text-emerald-900">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={executePayment}
                    onChange={(e) => setExecutePayment(e.target.checked)}
                  />
                  <span>
                    کسر مبلغ از حساب مبدأ و پرداخت مستقیم بانکی
                    <span className="block text-xs text-emerald-800/80 mt-0.5">
                      برای این حالت باید سشن اینترنت‌بانک صادرات فعال باشد.
                    </span>
                  </span>
                </label>
              ) : null}

              {needsBankSession ? (
                <div
                  className={`rounded-lg border px-3 py-2.5 text-sm ${
                    bsiStatusLoading
                      ? 'border-slate-200 bg-slate-50 text-slate-700'
                      : bsiReady
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                        : 'border-amber-200 bg-amber-50 text-amber-950'
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      {bsiStatusLoading ? (
                        <Loader2 size={18} className="animate-spin shrink-0 mt-0.5 text-slate-500" />
                      ) : bsiReady ? (
                        <ShieldCheck size={18} className="shrink-0 mt-0.5 text-emerald-600" />
                      ) : (
                        <ShieldAlert size={18} className="shrink-0 mt-0.5 text-amber-600" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium">
                          {bsiStatusLoading
                            ? 'در حال بررسی اتصال بانک…'
                            : bsiReady
                              ? 'اتصال اینترنت‌بانک فعال است'
                              : 'اتصال اینترنت‌بانک برقرار نیست'}
                        </p>
                        {!bsiStatusLoading && !bsiReady ? (
                          <p className="mt-1 text-xs leading-5 text-amber-900/90">
                            {bsiStatus?.configured === false
                              ? 'سرویس اینترنت‌بانک هنوز پیکربندی نشده است.'
                              : bsiStatus?.needs_otp
                                ? 'ورود نیمه‌کاره است؛ کد یک‌بارمصرف را در اینترنت‌بانک تکمیل کنید.'
                                : 'برای پرداخت مستقیم، ابتدا وارد اینترنت‌بانک شوید، بعد برگردید و همین فرم را ثبت کنید.'}
                          </p>
                        ) : null}
                        {bsiReady && bsiStatus?.username_masked ? (
                          <p className="mt-1 text-xs text-emerald-800/80">
                            کاربر: {bsiStatus.username_masked}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => void loadBsiStatus()}
                        disabled={bsiStatusLoading}
                        className="inline-flex items-center gap-1 rounded-lg border border-current/20 px-2.5 py-1.5 text-xs font-medium hover:bg-white/60 disabled:opacity-50"
                      >
                        {bsiStatusLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                        بررسی مجدد
                      </button>
                      {!bsiReady ? (
                        <Link
                          to={bsiServicePath('login')}
                          className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
                        >
                          ورود به اینترنت‌بانک
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  حساب مبدأ (شرکت){executePayment && !isEdit ? ' *' : ''}
                </label>
                <SearchableSelect
                  value={sourceBankId === '' ? '' : String(sourceBankId)}
                  onChange={(v) => setSourceBankId(v ? parseInt(v, 10) : '')}
                  options={[
                    { value: '', label: '— انتخاب نشده —' },
                    ...(meta?.company_bank_accounts ?? []).map((b) => ({
                      value: String(b.id),
                      label: `${b.name}${b.bank_name ? ` (${b.bank_name})` : ''}${b.account_number ? ` — ${b.account_number}` : ''}`,
                      searchText: `${b.name} ${b.bank_name ?? ''} ${b.account_number ?? ''}`,
                    })),
                  ]}
                  placeholder="جستجو و انتخاب حساب مبدأ..."
                  emptyMessage="حسابی یافت نشد"
                  inModal
                />
              </div>

              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="block text-sm font-medium text-gray-700">حساب مقصد (کارمند)</label>
                  <button
                    type="button"
                    onClick={() => setShowBankModal(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:bg-sky-50 px-2 py-1 rounded-lg"
                  >
                    <Plus size={14} />
                    تعریف حساب بانکی
                  </button>
                </div>
                {(meta?.employee_bank_accounts?.length ?? 0) > 0 ? (
                  <SearchableSelect
                    value={destBankId === '' ? '' : String(destBankId)}
                    onChange={(v) => setDestBankId(v ? parseInt(v, 10) : '')}
                    options={[
                      { value: '', label: '— انتخاب نشده —' },
                      ...(meta?.employee_bank_accounts ?? []).map((b) => {
                        const cardTail = b.card_number?.replace(/\D/g, '').slice(-4);
                        const parts = [
                          b.bank?.name ?? 'بانک',
                          b.account_holder_name,
                          cardTail ? `کارت …${cardTail}` : null,
                          b.iban ? 'شبا' : b.account_number ? `حساب ${b.account_number}` : null,
                          b.is_default ? '(اصلی)' : null,
                          b.is_active === false ? '(غیرفعال)' : null,
                        ].filter(Boolean);
                        return {
                          value: String(b.id),
                          label: parts.join(' — '),
                          searchText: `${b.bank?.name ?? ''} ${b.account_holder_name ?? ''} ${b.iban ?? ''} ${b.card_number ?? ''} ${b.account_number ?? ''}`,
                        };
                      }),
                    ]}
                    placeholder="جستجو و انتخاب حساب مقصد..."
                    emptyMessage="حسابی یافت نشد"
                    inModal
                  />
                ) : (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    حساب بانکی ثبت نشده. با دکمه «تعریف حساب بانکی» همین‌جا اضافه کنید.
                  </p>
                )}
                {selectedTransferMode ? (
                  <p
                    className={`mt-2 text-xs rounded-lg px-3 py-2 ${
                      selectedTransferMode === 'local'
                        ? 'bg-sky-50 text-sky-800 border border-sky-100'
                        : 'bg-violet-50 text-violet-800 border border-violet-100'
                    }`}
                  >
                    نوع انتقال پیشنهادی: {bsiTransferModeLabel(selectedTransferMode)}
                    {selectedTransferMode === 'local' && selectedDestBank?.account_number ? (
                      <span className="block mt-1 font-mono" dir="ltr">
                        شماره حساب مقصد: {selectedDestBank.account_number}
                      </span>
                    ) : null}
                    {selectedTransferMode === 'local' && bsiLocalDestinationMissingAccount(selectedDestBank ?? {}) ? (
                      <span className="block mt-1 text-rose-700">
                        برای انتقال داخل صادرات، شماره حساب (نه کارت) در پروفایل کارمند ثبت شود.
                      </span>
                    ) : null}
                  </p>
                ) : null}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">پیوست / فیش</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    beginAttachment(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                />
                {/* موبایل: مستقیم دوربین سخت‌افزاری */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    beginAttachment(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Upload size={16} />
                    آپلود فایل
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCamera(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-sky-200 bg-sky-50 text-sm text-sky-800 hover:bg-sky-100"
                  >
                    <Camera size={16} />
                    دوربین
                  </button>
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 sm:hidden"
                    title="دوربین گوشی"
                  >
                    دوربین گوشی
                  </button>
                  {(file || (existingAttachmentUrl && !removeExistingAttachment)) && (
                    <button
                      type="button"
                      onClick={clearAttachment}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-rose-200 text-sm text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 size={16} />
                      حذف
                    </button>
                  )}
                </div>
                {file ? (
                  <div className="mt-2 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-2">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="پیش‌نمایش فیش"
                        className="h-16 w-16 rounded-md object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-md bg-white border border-gray-200 text-gray-400">
                        <ImageIcon size={22} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1 text-xs text-gray-600 pt-1">
                      <p className="font-medium text-gray-800 truncate">{file.name}</p>
                      <p className="mt-0.5">{(file.size / 1024).toFixed(0)} کیلوبایت</p>
                    </div>
                  </div>
                ) : existingAttachmentUrl && !removeExistingAttachment ? (
                  <div className="mt-2 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-2">
                    {/\.(jpe?g|png|webp|gif)(\?|$)/i.test(existingAttachmentUrl + (existingAttachmentName ?? '')) ? (
                      <img
                        src={existingAttachmentUrl}
                        alt="فیش فعلی"
                        className="h-16 w-16 rounded-md object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-md bg-white border border-gray-200 text-gray-400">
                        <ImageIcon size={22} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1 text-xs text-gray-600">
                      <p className="font-medium text-gray-800 truncate">
                        {existingAttachmentName || 'پیوست فعلی'}
                      </p>
                      <p className="text-gray-500 mt-0.5">برای تعویض، فایل جدید آپلود کنید</p>
                    </div>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="اختیاری"
                />
              </div>
              </div>

              <div className="flex shrink-0 justify-end gap-2 border-t bg-white p-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-sky-600 text-white font-medium hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {isEdit ? 'ذخیره تغییرات' : `ثبت ${paymentTypeLabel}`}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <BankAccountModal
        show={showBankModal}
        onClose={() => setShowBankModal(false)}
        employeeId={employeeId}
        editAccount={null}
        banks={banks}
        onSuccess={() => {
          setShowBankModal(false);
          void handleBankSuccess();
        }}
        onError={(msg) => setFormError(msg)}
      />

      <CameraCaptureModal
        show={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={(f) => beginAttachment(f)}
      />

      <ReceiptCropModal
        show={showCrop}
        imageUrl={cropSrcUrl}
        onClose={closeCrop}
        onConfirm={(f) => {
          setAttachment(f);
          closeCrop();
        }}
        onSkip={() => {
          if (cropOriginalFile) setAttachment(cropOriginalFile);
          closeCrop();
        }}
      />
    </>
  );
};

export default AdvanceModal;
